import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import type {
  EmploymentProfileDTO,
  JdProfileLinkDTO,
  KpiProfileSummaryDTO,
  ProfileCurrentSalaryDTO,
  ProfileCurrentSalarySource,
  ReportingLineDTO,
} from "@/modules/users/domain/employee-profile.types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatSalaryGradeCodeLabelDisplay } from "@/shared/lib/format-salary-grade-display";
import { ProfileReportingLineBlock } from "./ProfileReportingLineBlock";

type ProfileEmploymentSectionProps = {
  reportingLine: ReportingLineDTO;
  employment: EmploymentProfileDTO | null;
  jd: JdProfileLinkDTO | null;
  currentSalary: ProfileCurrentSalaryDTO;
  kpiSummary: KpiProfileSummaryDTO;
  canViewJd: boolean;
  canViewKpiDashboard: boolean;
};

function formatProfileSalaryAmount(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function labelProfileSalarySource(
  source: ProfileCurrentSalarySource | null,
): string {
  switch (source) {
    case "position_band":
      return "ອີງຕາມສະເກລເງິນເດືອນຕຳແໜ່ງ (ຕາມອາຍຸເຮັດວຽກນັບຈາກຕຳແໜ່ງຫຼັກ)";
    case "promotion":
      return "ອີງຈາກການເລື່ອນຕຳແໜ່ງຫຼ້າສຸດ";
    case "previous_payroll":
      return "ອີງຈາກບັນທຶກເງິນເດືອນຫຼ້າສຸດ";
    default:
      return "";
  }
}

function ProfileCurrentSalaryBlock({
  currentSalary,
}: {
  currentSalary: ProfileCurrentSalaryDTO;
}) {
  return (
    <div className="text-sm">
      <p className="text-muted-foreground">ເງິນເດືອນພື້ນຖານ (ປັດຈຸບັນ)</p>
      <div className="mt-1 space-y-0.5">
        {currentSalary.amount != null && currentSalary.amount > 0 ? (
          <>
            <span className="font-medium tabular-nums">
              {formatProfileSalaryAmount(currentSalary.amount)} ກີບ
            </span>
            {currentSalary.source ? (
              <p className="text-muted-foreground text-xs">
                {labelProfileSalarySource(currentSalary.source)}
              </p>
            ) : null}
            {(() => {
              const gc = currentSalary.salaryGradeCode?.trim() ?? "";
              const gl = currentSalary.salaryGradeLabel?.trim() ?? "";
              if (gc === "" && gl === "") return null;
              return (
                <p className="text-muted-foreground text-xs">
                  ອ້າງເກຣດຕາມການຄຳນວນ:{" "}
                  {formatSalaryGradeCodeLabelDisplay(gc, gl)}
                </p>
              );
            })()}
          </>
        ) : (
          <span className="text-muted-foreground">
            ບໍ່ມີຂໍ້ມູນທີ່ຄຳນວນໄດ້ — ຕັ້ງສະເກລຕຳແໜ່ງ ຫຼື ບັນທຶກເງິນເດືອນ
          </span>
        )}
      </div>
    </div>
  );
}

export function ProfileEmploymentSection({
  reportingLine,
  employment,
  jd,
  currentSalary,
  kpiSummary,
  canViewJd,
  canViewKpiDashboard,
}: ProfileEmploymentSectionProps) {
  const nav = useNavigate();
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>ຂໍ້ມູນການເຮັດວຽກ</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-b pb-3">
          <h4 className="mb-2 font-medium text-sm">ສາຍງານ</h4>
          <ProfileReportingLineBlock reportingLine={reportingLine} />
        </div>

        {!employment ? (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີຕຳແໜ່ງຫຼັກໃນລະບົບ — ມອບໝາຍຕຳແໜ່ງຜ່ານເມນູກຳນົດຕຳແໜ່ງ / ໂຄງສ້າງອົງກອນ
          </p>
        ) : (
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">ຕຳແໜ່ງ</dt>
              <dd className="font-medium">{employment.positionTitle}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ພະແນກ</dt>
              <dd className="font-medium">
                {employment.departmentName ?? "—"}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">ເລີ່ມຕຳແໜ່ງ (ປັດຈຸບັນ)</dt>
              <dd>{formatDateLocal(employment.startDate)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">ລະດັບເງິນເດືອນຕຳແໜ່ງຫຼັກ</dt>
              <dd className="font-medium">
                {formatSalaryGradeCodeLabelDisplay(
                  employment.salaryGradeCode,
                  employment.salaryGradeLabel,
                )}
              </dd>
            </div>
          </dl>
        )}

        <ProfileCurrentSalaryBlock currentSalary={currentSalary} />

        {jd && canViewJd ? (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => nav({ to: `/app/jd/${jd.jdId}` })}
            >
              ເບິ່ງ JD: {jd.jdTitle ?? jd.jdId}
            </Button>
          </div>
        ) : null}
        {jd && !canViewJd ? (
          <p className="text-muted-foreground text-sm">
            ມີ JD ທີ່ຜູກກັບຕຳແໜ່ງ — ຕ້ອງມີສິດເບິ່ງ JD ຈຶ່ງຈະເປີດລິ້ງໄດ້
          </p>
        ) : null}

        <div className="border-t pt-3">
          <h4 className="mb-2 font-medium text-sm">ສະຫຼຸບ KPI (ມອບໝາຍລາຍບຸກຄົນ)</h4>
          {kpiSummary.totalActiveAssignments === 0 ? (
            <p className="text-muted-foreground text-sm">
              ບໍ່ມີ KPI ທີ່ມອບໝາຍແບບລາຍບຸກຄົນ
            </p>
          ) : (
            <>
              <p className="text-muted-foreground text-sm">
                ສະຖານະຫຼ້າສຸດຕໍ່ມອບໝາຍ: ຜ່ານ {kpiSummary.passCount} · ຕ່ຳກວ່າເປົ້າໝາຍ{" "}
                {kpiSummary.failCount} · ລໍຖ້າ {kpiSummary.pendingCount} ·
                ຍັງບໍ່ມີຜົນວັດ {kpiSummary.notMeasuredCount}
              </p>
              {canViewKpiDashboard ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => nav({ to: "/app/management/okr-kpi" })}
                >
                  ໄປພາບລວມ KPI
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
